import React, { useState, useMemo, useEffect } from "react";
import { MagnifyingGlassIcon, TrashIcon } from "@heroicons/react/24/outline";
import Table from "../../common/Table";
import { votersAPI } from "../../../services/api";
import toast from "react-hot-toast";
import ConfirmationModal from "../../common/ConfirmationModal";

function EditAssignedVotersTab({ voters = [] }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [votersPerPage, setVotersPerPage] = useState(5);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const [votersData, setVotersData] = useState([]);

  useEffect(() => {
    if (!Array.isArray(voters) || voters.length === 0) return;
    const mapped = voters.map((v, idx) => {
      const resident = v.resident || {};
      const firstName = v.first_name || v.firstName || "";
      const lastName = v.last_name || v.lastName || "";
      const fullName =
        resident.full_name ||
        v.name ||
        v.full_name ||
        `${firstName} ${lastName}`.trim();
      // Use numeric voter_id for deletion key
      const id = v.voter_id || resident.resident_id || v.id || idx;
      // Use formatted voters_id_no for display
      const voterId = v.voters_id_no || String(id);
      const initials = fullName
        ? fullName
            .split(" ")
            .filter(Boolean)
            .map((n) => n[0])
            .join("")
            .slice(0, 2)
            .toUpperCase()
        : "--";
      return {
        id,
        initials,
        name: fullName || "Unknown",
        voterId,
        phone: resident.contact_number || v.phone || v.mobile || "",
        status:
          typeof v.status === "string"
            ? v.status.charAt(0).toUpperCase() + v.status.slice(1)
            : "Active",
      };
    });
    setVotersData(mapped);
  }, [voters]);

  const handleDeleteVoter = async (id) => {
    try {
      await votersAPI.deleteVotersByPrecinctId(id);
      setVotersData((prev) => prev.filter((v) => v.id !== id));
      toast.success("Voter removed from precinct");
    } catch (e) {
      toast.error("Failed to remove voter");
    }
  };

  const openDeleteConfirm = (id) => {
    setDeletingId(id);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!deletingId) return;
    await handleDeleteVoter(deletingId);
    setDeletingId(null);
  };

  // Filter + search
  const filteredVoters = useMemo(() => {
    return votersData.filter(
      (v) =>
        v.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        v.voterId.includes(searchTerm)
    );
  }, [votersData, searchTerm]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredVoters.length / votersPerPage);
  const indexOfLastVoter = currentPage * votersPerPage;
  const indexOfFirstVoter = indexOfLastVoter - votersPerPage;
  const currentVoters = filteredVoters.slice(
    indexOfFirstVoter,
    indexOfLastVoter
  );

  const columns = [
    {
      key: "voter",
      label: "VOTER",
      render: (_, voter) => (
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-gradient-to-r from-blue-400 to-blue-600 flex items-center justify-center text-white font-semibold text-lg">
            {voter.initials}
          </div>
          <div>
            <p className="font-medium text-gray-800">{voter.name}</p>
            <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
              <span className="bg-blue-100 text-blue-700 font-semibold px-2 py-0.5 rounded">
                {voter.voterId}
              </span>
              <span>{voter.phone}</span>
            </div>
          </div>
        </div>
      ),
    },
    {
      key: "status",
      label: "STATUS",
      render: (_, voter) => (
        <div className="flex justify-end pr-4">
          <span className="px-3 py-1 rounded-full bg-green-100 text-green-700 text-sm">
            {voter.status}
          </span>
        </div>
      ),
    },
    {
      key: "action",
      label: "ACTION",
      render: (_, voter) => (
        <div className="flex justify-end pr-10">
          <button
            onClick={() => openDeleteConfirm(voter.id)}
            className="p-2 rounded-full hover:bg-red-100 active:bg-red-200 transition"
          >
            <TrashIcon className="w-5 h-5 text-red-500" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="p-6">
      {/* Search Bar */}
      <div className="relative w-full mb-4">
        <MagnifyingGlassIcon className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
        <input
          type="text"
          placeholder="Search voter by name or resident ID..."
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
        />
      </div>

      {/* Header */}
      <div className="grid grid-cols-3 bg-gray-100 border border-gray-300 rounded-t px-4 py-2 font-semibold text-gray-700 text-sm">
        <span>VOTER</span>
        <span className="text-right pr-9">STATUS</span>
        <span className="text-right pr-9">ACTION</span>
      </div>

      {/* Scrollable Voter List */}
      <div className="max-h-96 overflow-y-auto border-x border-b border-gray-300 rounded-b p-2 space-y-2">
        {currentVoters.length > 0 ? (
          <Table
            noContainer={true}
            columns={columns}
            data={currentVoters}
            getRowKey={(v) => v.id}
            pagination={
              filteredVoters.length > 0
                ? {
                    currentPage,
                    lastPage: totalPages,
                    perPage: votersPerPage,
                    total: filteredVoters.length,
                  }
                : null
            }
            onPageChange={(page, newPerPage = votersPerPage) => {
              setCurrentPage(page);
              if (newPerPage !== votersPerPage) {
                setVotersPerPage(newPerPage);
                setCurrentPage(1);
              }
            }}
            paginationProps={{
              onPerPageChange: (value) => {
                setVotersPerPage(value);
                setCurrentPage(1);
              },
              showSizeSelector: true,
            }}
          />
        ) : (
          <p className="text-gray-500 text-center py-4">No voters found.</p>
        )}
      </div>
      <ConfirmationModal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={confirmDelete}
        title="Remove assigned voter?"
        message="This action will remove the voter from this precinct. You can reassign later."
        confirmText="Remove"
        cancelText="Cancel"
        confirmButtonClass="bg-red-600 hover:bg-red-700 text-white"
      />
    </div>
  );
}

export default EditAssignedVotersTab;
